import { useMemo } from 'react';
import { Box, styled, useTheme } from '@mui/material';
import {
  dedupedInitialFromScore,
  InitialledScore,
} from 'pages/DownloadTeamWheel/scoreUtils';
import { useWheelContext } from 'pages/DownloadTeamWheel/hooks/useWheelContext';
import ColourOrderPreference from './ColourOrderPreference';
import TeamTableHeader from './TeamTableHeader';
import useSorting from './useSorting';
import getTableXCoordinates from './getTableXCoordinates';

const HEADER_HEIGHT = 16;
const ROW_HEIGHT = 12;

type TeamTableProps = {
  scores: (InitialledScore & {
    organisationId: string;
    organisationName: string;
  })[];
  svgId: string;
  test?: boolean;
};

const StyledSvg = styled('svg')(({ theme }) => ({
  fontFamily: theme.typography.fontFamily,
}));

const truncateString = (s: string) => {
  if (s.trim().length > 16) {
    return `${s.trim().substring(0, 15)}...`;
  }

  return s;
};

const MAX_NAME_VIEW_WIDTH = 100;

let sharedMeasureTextCanvas: HTMLCanvasElement | null = null;

const getMeasureTextContext = (): CanvasRenderingContext2D | null => {
  if (!sharedMeasureTextCanvas) {
    sharedMeasureTextCanvas = document.createElement('canvas');
  }
  // getContext is re-resolved from the canvas each call (rather than caching
  // the context itself) so it still picks up tests that re-mock it.
  return sharedMeasureTextCanvas.getContext('2d');
};

export const calculateStringPixelLength = (
  text: string,
  font: string,
): number => {
  const ctx = getMeasureTextContext();

  if (ctx) {
    ctx.font = font;
    return ctx.measureText(text).width;
  }
  return 0;
};

const truncateToPixelWidth = (text: string, maxWidth: number, font: string) => {
  const trimmed = text.trim();

  if (calculateStringPixelLength(trimmed, font) <= maxWidth) {
    return trimmed;
  }

  let low = 0;
  let high = trimmed.length;
  let longestFittingEnd = 0;

  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    const candidate = `${trimmed.slice(0, mid).trimEnd()}...`;

    if (calculateStringPixelLength(candidate, font) <= maxWidth) {
      longestFittingEnd = mid;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }

  return `${trimmed.slice(0, longestFittingEnd).trimEnd()}...`;
};

const TeamTable = ({ scores, svgId, test = false }: TeamTableProps) => {
  const theme = useTheme();
  const [wheelState] = useWheelContext();
  const { namesColumns, isMultiOrg } = wheelState;
  const { showNumbers, showOrganisations } = namesColumns;
  const padding = 5;
  const minNamePixelLength = 80;
  const pixelFontSize = 6;
  const font = `${pixelFontSize}px ${theme.typography.fontFamily}`;

  const largestSvgStringPixelLength = Math.max(
    ...scores.map(
      (score) =>
        Math.min(
          calculateStringPixelLength(score.fullName, font),
          MAX_NAME_VIEW_WIDTH,
        ) + padding,
    ),
    minNamePixelLength,
  );

  const fullHeight = HEADER_HEIGHT + ROW_HEIGHT * scores.length;
  const { columns, lengthRemoved, columnLengths } = getTableXCoordinates(
    showNumbers,
    largestSvgStringPixelLength,
    isMultiOrg && showOrganisations,
  );
  const minWidth = 195 - lengthRemoved;
  const sortedScores = useSorting(scores, wheelState);
  const isConscious = wheelState.scoreType === 'CONSCIOUS';

  const truncatedNamesByScore = useMemo(() => {
    const truncatedNames = new Map<string, string>();
    scores.forEach((score) => {
      truncatedNames.set(
        `${score.initials}-${score.duplicateNumber}`,
        truncateToPixelWidth(score.fullName, columnLengths.name, font),
      );
    });
    return truncatedNames;
  }, [scores, columnLengths.name, font]);

  return (
    <Box
      sx={{
        p: 2,
      }}
    >
      <StyledSvg
        viewBox={`0 0 ${minWidth + largestSvgStringPixelLength + 4}
        ${fullHeight + 4}`}
        id={svgId}
      >
        <g id="rowGroup" transform="translate(1, 1)" role="table">
          <rect
            width={minWidth + largestSvgStringPixelLength}
            height={fullHeight}
            fill={theme.palette.grey[100]}
          />
          {sortedScores.length > 1 && (
            <rect
              stroke={theme.palette.blue.main}
              width={minWidth + largestSvgStringPixelLength}
              height={fullHeight}
              strokeWidth="0.5"
              fill="none"
            />
          )}
          <TeamTableHeader
            showNumbers={showNumbers}
            showOrganisations={showOrganisations}
            largestNameWidth={largestSvgStringPixelLength}
            isMultiOrg={isMultiOrg}
          />
          {sortedScores.map((score, i: number) => {
            const position = isConscious
              ? score.wheelPositions.conscious
              : score.wheelPositions.lessConscious;
            const colours = isConscious
              ? score.colourScores.conscious
              : score.colourScores.lessConscious;
            const dedupedInitial = dedupedInitialFromScore(score).split('-');
            return (
              <g
                transform={`translate(0 ${ROW_HEIGHT * (i + 2)})`}
                key={`${score.initials}-${score.duplicateNumber}`}
              >
                <g role="row">
                  <text fontSize="6px" textAnchor="start">
                    {showNumbers && (
                      <tspan
                        role="cell"
                        x={columns.numbers}
                        data-testid="team-table-number"
                      >
                        {String(score.index + 1)}
                      </tspan>
                    )}
                    <tspan role="cell" x={columns.initials}>
                      {dedupedInitial[0]}
                      <tspan
                        baselineShift="super"
                        fontSize="4px"
                        data-testid={
                          test
                            ? `${dedupedInitial[0]}-${dedupedInitial[1]}-table-superscript`
                            : null
                        }
                      >
                        {parseInt(dedupedInitial[1], 10) > 0 &&
                          dedupedInitial[1]}
                      </tspan>
                    </tspan>
                    <tspan
                      role="cell"
                      x={columns.name}
                      data-testid="team-table-name"
                    >
                      {truncatedNamesByScore.get(
                        `${score.initials}-${score.duplicateNumber}`,
                      )}
                    </tspan>
                    {isMultiOrg && showOrganisations && (
                      <tspan
                        role="cell"
                        x={columns.organisation}
                        data-testid="team-table-organisation"
                      >
                        {truncateString(score.organisationName)}
                      </tspan>
                    )}
                    <tspan
                      role="cell"
                      x={columns.position}
                      data-testid="team-table-position"
                    >
                      {position}
                    </tspan>
                  </text>
                  <g
                    role="cell"
                    transform={`translate(${columns.colourEnergies - 2} -6)`}
                  >
                    <ColourOrderPreference colours={colours} test={test} />
                  </g>
                  <line
                    stroke={theme.palette.blue.main}
                    strokeWidth="0.5"
                    x1="0"
                    y1="4"
                    x2={minWidth + largestSvgStringPixelLength}
                    y2="4"
                  />
                </g>
              </g>
            );
          })}
        </g>
      </StyledSvg>
    </Box>
  );
};

export default TeamTable;
